import React, { useState } from 'react';
import { format } from 'date-fns';
import { Loader2 } from 'lucide-react';
import useAppStore from '../store';
import SearchableSelect from './SearchableSelect';
import { NewDumpsterTask } from '../types';

interface DumpsterTaskQuickCreateFormProps {
  clientId: string;
  locationId: string;
  onSubmit: () => void;
  onCancel: () => void;
}

const DumpsterTaskQuickCreateForm: React.FC<DumpsterTaskQuickCreateFormProps> = ({
  clientId,
  locationId,
  onSubmit,
  onCancel
}) => {
  const { drivers, dumpsters, addTask, currentUser } = useAppStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    driverId: '',
    dumpsterId: '',
    date: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
    dumpsterTaskType: 'pose' as 'pose' | 'retrait' | 'echange',
    comment: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentUser?.id) {
      alert('Vous devez être connecté pour créer une tâche');
      return;
    }

    setIsSubmitting(true);

    try {
      const taskData: NewDumpsterTask = {
        driverId: formData.driverId,
        clientId,
        locationId,
        date: formData.date,
        type: 'dumpster',
        dumpsterId: formData.dumpsterId || undefined,
        dumpsterTaskType: formData.dumpsterTaskType,
        comment: formData.comment.trim() || undefined
      };

      // Log pour déboguer
      console.log('Submitting quick dumpster task with comment:', formData.comment);
      console.log('Final task object:', taskData);

      await addTask(taskData);

      onSubmit();
    } catch (error) {
      console.error('Error creating task:', error);
      alert('Une erreur est survenue lors de la création de la tâche');
    } finally {
      setIsSubmitting(false);
    }
  };

  const driverOptions = drivers.map(driver => ({
    value: driver.id,
    label: `${driver.firstName} ${driver.lastName}`
  })).sort((a, b) => {
    const driverA = drivers.find(d => d.id === a.value);
    const driverB = drivers.find(d => d.id === b.value);
    if (!driverA || !driverB) return 0;

    // Tri par nom de famille, puis par prénom
    const lastNameComparison = driverA.lastName.localeCompare(driverB.lastName);
    if (lastNameComparison !== 0) return lastNameComparison;
    return driverA.firstName.localeCompare(driverB.firstName);
  });

  const dumpsterOptions = dumpsters.map(dumpster => ({
    value: dumpster.id,
    label: `${dumpster.name} (${dumpster.number})`
  })).filter(option => {
    const dumpster = dumpsters.find(d => d.id === option.value);
    return dumpster?.isActive !== false;
  });

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
      <h2 className="text-xl font-bold mb-4">Créer une tâche benne</h2>
      
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div>
            <label className="block text-gray-700 font-medium mb-2">
              Chauffeur
            </label>
            <SearchableSelect
              options={driverOptions}
              value={formData.driverId}
              onChange={(value) => setFormData(prev => ({ ...prev, driverId: value }))}
              placeholder="Sélectionner un chauffeur"
              searchPlaceholder="Rechercher un chauffeur..."
            />
          </div>

          <div>
            <label className="block text-gray-700 font-medium mb-2">
              Benne
            </label>
            <SearchableSelect
              options={dumpsterOptions}
              value={formData.dumpsterId}
              onChange={(value) => setFormData(prev => ({ ...prev, dumpsterId: value }))}
              placeholder="Sélectionner une benne (optionnel)"
              searchPlaceholder="Rechercher une benne..."
            />
            <p className="text-xs text-gray-500 mt-1">
              Si aucune benne n'est sélectionnée, le chauffeur devra en choisir une au démarrage.
            </p>
          </div>

          <div>
            <label className="block text-gray-700 font-medium mb-2">
              Type d'opération
            </label>
            <select
              value={formData.dumpsterTaskType}
              onChange={(e) => setFormData(prev => ({ 
                ...prev, 
                dumpsterTaskType: e.target.value as 'pose' | 'retrait' | 'echange'
              }))}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="pose">Pose</option>
              <option value="retrait">Retrait</option>
              <option value="echange">Échange</option>
            </select>
          </div>

          <div>
            <label className="block text-gray-700 font-medium mb-2">
              Date et heure
            </label>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="date"
                value={formData.date.split('T')[0]}
                onChange={(e) => {
                  const time = formData.date.split('T')[1] || '07:00';
                  setFormData(prev => ({
                    ...prev,
                    date: `${e.target.value}T${time}`
                  }));
                }}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <select
                value={formData.date.split('T')[1]?.slice(0, 5) || '07:00'}
                onChange={(e) => {
                  const date = formData.date.split('T')[0];
                  setFormData(prev => ({
                    ...prev,
                    date: `${date}T${e.target.value}`
                  }));
                }}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                {Array.from({ length: 49 }, (_, i) => {
                  const hour = Math.floor(i / 4) + 7;
                  const minute = (i % 4) * 15;
                  const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                  return hour < 20 && <option key={time} value={time}>{time}</option>;
                })}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-gray-700 font-medium mb-2">
              Commentaire (facultatif)
            </label>
            <textarea
              value={formData.comment}
              onChange={(e) => setFormData(prev => ({ ...prev, comment: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              placeholder="Instructions spécifiques pour le chauffeur..."
              maxLength={500}
            />
            <div className="text-xs text-gray-500 mt-1 text-right">
              {formData.comment.length}/500 caractères
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-2 mt-6">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Annuler
          </button>

          <button
            type="submit"
            disabled={isSubmitting}
            className="px-4 py-2 bg-blue-800 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
            <span>{isSubmitting ? 'Création en cours...' : 'Créer'}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default DumpsterTaskQuickCreateForm;